import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import axios from 'axios';
import API_BASE_URL from '../../../services/api';


const EditDoctorModal = ({ isOpen, onClose, onSave, doctor }) => {
  const [formData, setFormData] = useState({
    initial: 'Dr.',
    name: '',
    qualification: '',
    specialityType: '',
    speciality: '',
    mobile: '',
    landLine: '',
    email: '',
    venue: '',
    refCenter: '',
    source: '',
    address: '',
    otherInfo: '',
    
  });

  const [loading, setLoading] = useState(false);

  // Populate form when doctor data changes
  useEffect(() => {
    if (doctor) {
      setFormData({
        initial: doctor.initial || 'Dr.',
        name: doctor.name || '',
        qualification: doctor.qualification || '',
        specialityType: doctor.specialityType || '',
        speciality: doctor.speciality || '',
        mobile: doctor.mobile || '',
        landLine: doctor.landLine || '',
        email: doctor.email || '',
        venue: doctor.venue || '',
        refCenter: doctor.refCenter || '',
        source: doctor.source || '',
        address: doctor.address || '',
        otherInfo: doctor.otherInfo || '',
      
      });
    }
  }, [doctor]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        initial: 'Dr.',
        name: '',
        qualification: '',
        specialityType: '',
        speciality: '',
        mobile: '',
        landLine: '',
        email: '',
        venue: '',
        refCenter: '',
        source: '',
        address: '',
        otherInfo: '',
      
      });
    }
  }, [isOpen]);

  if (!isOpen || !doctor) return null;

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...formData,
      };

      const response = await axios.put(
        `${API_BASE_URL}/api/doctors/${doctor.id}`,
        payload
      );

      if (response.status === 200) {
        onSave(response.data.doctor);
        onClose();
      }

    } catch (error) {
      console.error("Doctor update failed:", error);
      alert(error.response?.data?.error || "Failed to update doctor");
    } finally {
      setLoading(false);
    }
  };

  const initials = ['Dr.'];
  const specialityTypes = ['Medical', 'Surgical', 'Gynecology', 'Pediatrics', 'Orthopedics', 'Cardiology', 'Neurology', 'Dermatology'];
  const venues = ['NAVAMI DIAGNOSTICS', 'NAVAMI KNMH', 'Private Clinic', 'Hospital', 'Other'];

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Overlay */}
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose}></div>

        {/* Modal */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            {/* Header */}
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Edit Doctor</h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                {/* Personal Information */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Title</label>
                    <select
                      value={formData.initial}
                      onChange={(e) => handleChange('initial', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {initials.map(title => (
                        <option key={title} value={title}>{title}</option>
                      ))}
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                </div>

                {/* Qualification & Speciality */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Qualification</label>
                    <input
                      type="text"
                      value={formData.qualification}
                      onChange={(e) => handleChange('qualification', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="MD, MBBS, MS, etc."
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Speciality Type</label>
                    <select
                      value={formData.specialityType}
                      onChange={(e) => handleChange('specialityType', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select Type</option>
                      {specialityTypes.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Speciality</label>
                  <input
                    type="text"
                    value={formData.speciality}
                    onChange={(e) => handleChange('speciality', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Cardiologist, Neurologist, etc."
                  />
                </div>

                {/* Contact Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Mobile</label>
                    <div className="flex">
                      <div className="flex items-center px-2 py-2 bg-gray-100 border border-r-0 border-gray-300 rounded-l text-xs text-gray-600 font-medium">
                        +91
                      </div>
                      <input
                        type="tel"
                        value={formData.mobile}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, "");
                          if (value.length <= 10) {
                            handleChange('mobile', value);
                          }
                        }}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-r text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        maxLength="10"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Landline</label>
                    <input
                      type="tel"
                      value={formData.landLine}
                      onChange={(e) => handleChange('landLine', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

               

                {/* Source */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Source</label>
                  <input
                    type="text"
                    value={formData.source}
                    onChange={(e) => handleChange('source', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="How did you get this doctor's information?"
                  />
                </div>

                {/* Address */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Address</label>
                  <textarea
                    value={formData.address}
                    onChange={(e) => handleChange('address', e.target.value)}
                    rows="2"
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Other Info */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Other Information</label>
                  <textarea
                    value={formData.otherInfo}
                    onChange={(e) => handleChange('otherInfo', e.target.value)}
                    rows="2"
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

               
              </div>

              {/* Footer Buttons */}
              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={loading}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent mr-2"></span>
                      Updating...
                    </>
                  ) : (
                    'Update Doctor'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditDoctorModal;